import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import { Formik } from 'formik';
import * as yup from 'yup';
import history from '../../history';
import swal from 'sweetalert';
import { Form, Input, Button, Checkbox } from 'semantic-ui-react';
import { createGroupTour, updateGroupTour, showGroupTour, stopBatchGroupTour, stopSeriesGroupTour } from '../../api/groupToursApi';
import { SUPPORTED_CURRENCIES, formatMoneyLegacy } from '../../utils/currency';

const schema = yup.object().shape({
  name: yup.string().required('Name is required'),
  vendor_total_price: yup.number().required('Required').min(0.01, 'Must be > 0'),
  currency_code: yup.string().required('Currency is required').oneOf(['NPR', 'USD'], 'Must be NPR or USD'),
  markup_percentage: yup.number().nullable().min(0, 'Must be ≥ 0'),
  interest_percentage: yup.number().nullable().min(0, 'Must be ≥ 0').max(100, 'Must be ≤ 100'),
  min_group_size: yup.number().required('Required').integer().min(1, 'Must be ≥ 1'),
  max_group_size: yup.number().nullable().integer().min(1).test('gte-min', 'Max must be ≥ min group size', function (v) {
    if (v == null || v === '') return true;
    const m = this.parent.min_group_size;
    return m == null || Number(v) >= Number(m);
  }),
  cutoff_days_before_start: yup.number().required('Required').integer('Must be a whole number').min(0, 'Must be 0 or greater'),
  start_date: yup.string().nullable(),
  end_date: yup.string().nullable().test('after-start', 'End date must be on or after start date', function (v) {
    const s = this.parent.start_date;
    if (!s || !v) return true;
    return v >= s;
  }),
  recurrence_days: yup.number().nullable().integer().min(1, 'Must be ≥ 1')
});

class GroupTourForm extends Component {
  state = { groupTour: null, loading: true, fetchError: null, recurrenceUpdating: false };

  componentDidMount() {
    const idx = this.props.match && this.props.match.params && this.props.match.params.idx;
    const isCreate = !idx || idx === 'new';
    const fromState = this.props.location.state && this.props.location.state.groupTour;
    if (fromState && fromState.idx === idx) {
      this.setState({ groupTour: fromState, loading: false, fetchError: null });
      return;
    }
    if (idx && !isCreate) {
      showGroupTour(idx)
        .then((r) => {
          const data = (r.data && r.data.idx != null) ? r.data : (r.data && r.data.group_tour) || r.data;
          this.setState({ groupTour: data || null, loading: false, fetchError: null });
        })
        .catch(() => this.setState({ loading: false, fetchError: 'Failed to load group tour' }));
    } else {
      this.setState({ loading: !isCreate, fetchError: null });
    }
  }

  refreshTour = () => {
    const idx = this.props.match && this.props.match.params && this.props.match.params.idx;
    if (!idx || idx === 'new') return;
    showGroupTour(idx)
      .then((r) => {
        const data = (r.data && r.data.idx != null) ? r.data : (r.data && r.data.group_tour) || r.data;
        this.setState({ groupTour: data || null });
      })
      .catch(() => {});
  };

  handleStopBatch = () => {
    const { groupTour } = this.state;
    if (!groupTour || !groupTour.idx) return;
    this.setState({ recurrenceUpdating: true });
    stopBatchGroupTour(groupTour.idx)
      .then(() => { swal('Done', 'This batch will not create future batches.', 'success'); this.refreshTour(); })
      .catch((e) => swal('Error', (e.response?.data?.errors || [])?.join(' ') || 'Failed', 'error'))
      .finally(() => this.setState({ recurrenceUpdating: false }));
  };

  handleStopSeries = () => {
    const { groupTour } = this.state;
    if (!groupTour || !groupTour.idx) return;
    swal({ title: 'Stop entire series?', text: 'No further batches will be created for this recurring trip. Existing batches remain unchanged.', icon: 'warning', buttons: true, dangerMode: true })
      .then((ok) => {
        if (!ok) return;
        this.setState({ recurrenceUpdating: true });
        stopSeriesGroupTour(groupTour.idx)
          .then(() => { swal('Done', 'Recurrence stopped for this series.', 'success'); this.refreshTour(); })
          .catch((e) => swal('Error', (e.response?.data?.errors || [])?.join(' ') || 'Failed', 'error'))
          .finally(() => this.setState({ recurrenceUpdating: false }));
      });
  };

  getBackTo() {
    return this.props.backTo || '/admin/group_tours';
  }

  getSuccessTo() {
    return this.props.successTo || '/admin/group_tours';
  }

  getInitial() {
    const g = this.state.groupTour || {};
    const toDateValue = (d) => (d && String(d).length >= 10 ? String(d).substring(0, 10) : '');
    const cc = g.currency_code || (g.vendor_total_price_money && g.vendor_total_price_money.currency_code) || 'NPR';
    return {
      name: g.name || '',
      description: g.description || '',
      destination: g.destination || '',
      origin: g.origin || '',
      start_date: toDateValue(g.start_date),
      end_date: toDateValue(g.end_date),
      vendor_total_price: g.vendor_total_price != null ? g.vendor_total_price : (g.total_amount != null ? g.total_amount : ''),
      currency_code: cc,
      markup_percentage: g.markup_percentage != null && g.markup_percentage !== '' ? g.markup_percentage : '',
      interest_percentage: g.interest_percentage != null && g.interest_percentage !== '' ? g.interest_percentage : '',
      min_group_size: g.min_group_size != null ? g.min_group_size : 2,
      max_group_size: g.max_group_size != null && g.max_group_size !== '' ? g.max_group_size : '',
      cutoff_days_before_start: g.cutoff_days_before_start != null ? g.cutoff_days_before_start : '',
      published: !!g.published,
      auto_create_next: !!g.auto_create_next,
      recurrence_days: g.recurrence_days != null && g.recurrence_days !== '' ? Number(g.recurrence_days) : 7
    };
  }

  isVendor() {
    return (this.getBackTo() || '').includes('partner');
  }

  render() {
    const { groupTour, loading, fetchError } = this.state;
    const idx = this.props.match && this.props.match.params && this.props.match.params.idx;
    const isEdit = !!idx && idx !== 'new';

    // In edit mode: wait for data to load
    if (isEdit && loading) {
      return <div className="container p-4">Loading...</div>;
    }

    // Show error if fetch failed
    if (isEdit && fetchError) {
      return (
        <div className="container">
          <Link to={this.getBackTo()} className="btn btn-outline-secondary btn-sm mb-3">← Back to Group Tours</Link>
          <div className="alert alert-danger">{fetchError}</div>
        </div>
      );
    }

    // Show error if not found (edit mode but no data after loading)
    if (isEdit && !groupTour) {
      return (
        <div className="container">
          <Link to={this.getBackTo()} className="btn btn-outline-secondary btn-sm mb-3">← Back to Group Tours</Link>
          <div className="alert alert-danger">Group tour not found.</div>
        </div>
      );
    }

    const backTo = this.getBackTo();
    const successTo = this.getSuccessTo();

    return (
      <div className="container">
        <Link to={backTo} className="btn btn-outline-secondary btn-sm mb-3">← Back to Group Tours</Link>
        <div className="card">
          <div className="card-body">
            <h3 className="title">{isEdit ? 'Edit Group Tour' : 'New Group Tour'}</h3>
            {isEdit && groupTour && groupTour.has_any_booking && (
              <div className="alert alert-warning mb-3">
                <strong>Editing is locked.</strong> This trip has registrations. You cannot change details. Use <strong>Stop This Batch</strong> or <strong>Stop Entire Series</strong> below for recurring trips only.
              </div>
            )}
            <Formik
              initialValues={this.getInitial()}
              validationSchema={schema}
              enableReinitialize
              onSubmit={(values, { setSubmitting }) => {
                const payload = { ...values };
                payload.max_group_size = (values.max_group_size != null && values.max_group_size !== '') ? Number(values.max_group_size) : null;
                payload.cutoff_days_before_start = Math.max(0, parseInt(values.cutoff_days_before_start, 10));
                payload.auto_create_next = !!values.auto_create_next;
                payload.recurrence_days = values.recurrence_days != null && values.recurrence_days !== '' ? Math.max(1, Number(values.recurrence_days)) : 7;
                const major = parseFloat(values.vendor_total_price);
                const cc = (values.currency_code || 'NPR').toUpperCase();
                payload.vendor_total_price_minor = Number.isFinite(major) ? Math.round(major * 100) : undefined;
                payload.currency_code = cc;
                if (this.isVendor()) {
                  delete payload.markup_percentage;
                  delete payload.interest_percentage;
                }
                payload.markup_percentage = (payload.markup_percentage !== '' && payload.markup_percentage != null) ? Number(payload.markup_percentage) : null;
                payload.interest_percentage = (payload.interest_percentage !== '' && payload.interest_percentage != null) ? Number(payload.interest_percentage) : null;
                const successTo = this.getSuccessTo();
                if (isEdit) {
                  updateGroupTour(idx, payload)
                    .then(() => { swal('Updated', { icon: 'success' }); history.push(successTo); })
                    .catch((e) => { swal('Error', (e.response && e.response.data && e.response.data.errors && e.response.data.errors.join(' ')) || 'Update failed', { icon: 'error' }); })
                    .finally(() => setSubmitting(false));
                } else {
                  createGroupTour(payload)
                    .then(() => { swal('Created', { icon: 'success' }); history.push(successTo); })
                    .catch((e) => { swal('Error', (e.response && e.response.data && e.response.data.errors && e.response.data.errors.join(' ')) || 'Create failed', { icon: 'error' }); })
                    .finally(() => setSubmitting(false));
                }
              }}
            >
              {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue, isSubmitting }) => {
                const formLocked = isEdit && groupTour && groupTour.has_any_booking;
                return (
                <Form onSubmit={handleSubmit}>
                  <Form.Field error={touched.name && errors.name}>
                    <label>Name *</label>
                    <Input name="name" value={values.name} onChange={handleChange} onBlur={handleBlur} disabled={formLocked} />
                    {touched.name && errors.name && <span className="text-danger small">{errors.name}</span>}
                  </Form.Field>
                  <Form.Field>
                    <label>Description</label>
                    <Form.TextArea name="description" value={values.description} onChange={(e, { value }) => setFieldValue('description', value)} onBlur={handleBlur} rows={3} disabled={formLocked} />
                  </Form.Field>
                  <Form.Field>
                    <label>From (origin)</label>
                    <Input name="origin" value={values.origin} onChange={handleChange} onBlur={handleBlur} placeholder="e.g. Kathmandu" disabled={formLocked} />
                  </Form.Field>
                  <Form.Field>
                    <label>To (destination)</label>
                    <Input name="destination" value={values.destination} onChange={handleChange} onBlur={handleBlur} placeholder="e.g. Annapurna Base Camp" disabled={formLocked} />
                  </Form.Field>
                  <Form.Field error={touched.end_date && errors.end_date}>
                    <label>Start date</label>
                    <Input type="date" name="start_date" value={values.start_date || ''} onChange={handleChange} onBlur={handleBlur} disabled={formLocked} />
                  </Form.Field>
                  <Form.Field error={touched.end_date && errors.end_date}>
                    <label>End date</label>
                    <Input type="date" name="end_date" value={values.end_date || ''} onChange={handleChange} onBlur={handleBlur} disabled={formLocked} />
                    {touched.end_date && errors.end_date && <span className="text-danger small">{errors.end_date}</span>}
                  </Form.Field>
                  <Form.Field error={touched.currency_code && errors.currency_code}>
                    <label>Currency *</label>
                    <Form.Select
                      options={SUPPORTED_CURRENCIES.map(({ code, label }) => ({ key: code, value: code, text: label }))}
                      value={values.currency_code || 'NPR'}
                      onChange={(e, { value }) => setFieldValue('currency_code', value)}
                      disabled={formLocked}
                      placeholder="Select currency"
                    />
                    {formLocked && <span className="text-muted small d-block mt-1">Form is read-only while registrations exist.</span>}
                    {touched.currency_code && errors.currency_code && <span className="text-danger small">{errors.currency_code}</span>}
                  </Form.Field>
                  <Form.Field error={touched.vendor_total_price && errors.vendor_total_price}>
                    <label>Total price you are asking (per person) *</label>
                    <Input type="number" min={0.01} step="0.01" name="vendor_total_price" value={values.vendor_total_price} onChange={handleChange} onBlur={handleBlur} placeholder="Your price per person" disabled={formLocked} />
                    {touched.vendor_total_price && errors.vendor_total_price && <span className="text-danger small">{errors.vendor_total_price}</span>}
                    <span className="text-muted small d-block mt-1">{values.currency_code === 'USD' ? 'USD per person.' : 'NPR per person.'} Platform may add markup; customers pay interest then rest.</span>
                  </Form.Field>
                  {!this.isVendor() && (
                    <>
                      <Form.Field error={touched.markup_percentage && errors.markup_percentage}>
                        <label>Markup % (admin)</label>
                        <Input type="number" min={0} step="0.01" name="markup_percentage" value={values.markup_percentage || ''} onChange={handleChange} onBlur={handleBlur} placeholder="Blank = use platform default" disabled={formLocked} />
                        {touched.markup_percentage && errors.markup_percentage && <span className="text-danger small">{errors.markup_percentage}</span>}
                      </Form.Field>
                      <Form.Field error={touched.interest_percentage && errors.interest_percentage}>
                        <label>Interest % (admin)</label>
                        <Input type="number" min={0} max={100} step="0.01" name="interest_percentage" value={values.interest_percentage || ''} onChange={handleChange} onBlur={handleBlur} placeholder="Blank = use platform default" disabled={formLocked} />
                        {touched.interest_percentage && errors.interest_percentage && <span className="text-danger small">{errors.interest_percentage}</span>}
                      </Form.Field>
                    </>
                  )}
                  <Form.Field error={touched.min_group_size && errors.min_group_size}>
                    <label>Min group size *</label>
                    <Input type="number" min={1} name="min_group_size" value={values.min_group_size} onChange={handleChange} onBlur={handleBlur} disabled={formLocked} />
                    {touched.min_group_size && errors.min_group_size && <span className="text-danger small">{errors.min_group_size}</span>}
                  </Form.Field>
                  <Form.Field error={touched.max_group_size && errors.max_group_size}>
                    <label>Max group size (optional)</label>
                    <Input type="number" min={1} name="max_group_size" value={values.max_group_size || ''} onChange={handleChange} onBlur={handleBlur} placeholder="Leave blank for no cap" disabled={formLocked} />
                    {touched.max_group_size && errors.max_group_size && <span className="text-danger small">{errors.max_group_size}</span>}
                    <span className="text-muted small d-block mt-1">When set, registrations close once this many paid people are reached. Set equal to min to &quot;close when group is formed&quot;.</span>
                  </Form.Field>
                  <Form.Field error={touched.cutoff_days_before_start && errors.cutoff_days_before_start}>
                    <label>Registration closes (days before start) *</label>
                    <Input type="number" min={0} step={1} name="cutoff_days_before_start" value={values.cutoff_days_before_start ?? ''} onChange={handleChange} onBlur={handleBlur} placeholder="e.g. 2" disabled={formLocked} />
                    <span className="text-muted small d-block mt-1">Number of days before the tour start when registrations automatically close so you can begin operations and receive the final participant list.</span>
                    {touched.cutoff_days_before_start && errors.cutoff_days_before_start && <span className="text-danger small">{errors.cutoff_days_before_start}</span>}
                  </Form.Field>
                  <h4 className="mt-4 mb-2">Recurrence Settings</h4>
                  {isEdit && groupTour.template_id && (
                    <div className="mb-3 p-2 bg-light rounded">
                      <strong>Recurrence</strong>
                      {groupTour.series_stopped ? (
                        <span className="ml-2 badge badge-secondary">Recurrence stopped for this series</span>
                      ) : (
                        <div className="mt-1">
                          {!groupTour.auto_create_next && <p className="text-muted small mb-1">This batch will not create future batches.</p>}
                          <button type="button" className="btn btn-outline-secondary btn-sm mr-1" onClick={this.handleStopBatch} disabled={this.state.recurrenceUpdating || !groupTour.auto_create_next}>Stop This Batch</button>
                          <button type="button" className="btn btn-outline-warning btn-sm" onClick={this.handleStopSeries} disabled={this.state.recurrenceUpdating}>Stop Entire Series</button>
                        </div>
                      )}
                    </div>
                  )}
                  <p className="text-muted small mb-2">Automatically create next batch when full or registration closes.</p>
                  <Form.Field>
                    <Checkbox
                      label="Auto create next batch"
                      checked={!!values.auto_create_next}
                      onChange={(e, { checked }) => setFieldValue('auto_create_next', checked)}
                      disabled={formLocked}
                    />
                  </Form.Field>
                  <Form.Field error={touched.recurrence_days && errors.recurrence_days}>
                    <label>Repeat every (days)</label>
                    <Input
                      type="number"
                      min={1}
                      name="recurrence_days"
                      value={values.recurrence_days ?? 7}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={formLocked || !values.auto_create_next}
                      placeholder="7"
                    />
                    {touched.recurrence_days && errors.recurrence_days && <span className="text-danger small">{errors.recurrence_days}</span>}
                  </Form.Field>
                  <Button type="submit" primary disabled={isSubmitting || formLocked}>{isEdit ? 'Update' : 'Create'}</Button>
                  <Button type="button" onClick={() => history.push(this.getBackTo())} className="ml-2">Cancel</Button>
                </Form>
              );
              }}
            </Formik>
          </div>
        </div>
      </div>
    );
  }
}

export default GroupTourForm;
